import { v4 as uuidv4 } from "uuid";
import { ActivitiesResponseTypeEnum, type ActivitiesAnswerResponse } from "@simbelapp/order-sdk";
import { QuizGapSegmentKindEnum, QuizTypeEnum } from "~/utils/enums/activities.enums";
import type { IActivityTypeMeta, IQuizTypeMeta } from "~/utils/interfaces/quiz-interfaces";

// Every question type, wherever it sits: an LMS training, an enquête or a questionnaire.
export const QUIZ_TYPE_META: Record<QuizTypeEnum, IQuizTypeMeta> = {
  [QuizTypeEnum.OPEN_QUESTION]: { label: "Question ouverte", icon: "quiz_open", scored: false },
  [QuizTypeEnum.MULTIPLE_CHOICE_QUESTION]: { label: "Choix multiples", icon: "quiz_multiple", scored: true },
  [QuizTypeEnum.ASSOCIATION_QUESTION]: { label: "Association", icon: "quiz_associations", scored: true },
  [QuizTypeEnum.GAP_FILLING_QUESTION]: { label: "Texte à trou", icon: "quiz_gap_fill_text", scored: true },
  [QuizTypeEnum.RATING]: {
    label: "Notation 1 à 10",
    shortLabel: "Note 1 à 10",
    title: "Notation de 1 à 10",
    icon: "star",
    scored: false,
  },
  [QuizTypeEnum.MULTIPLE_CHOICE_UNSCORED]: { label: "Choix multiples", icon: "quiz_multiple", scored: false },
};

export const QUIZ_TYPES = Object.values(QuizTypeEnum);

// What each builder offers, in the order it offers them.
export const LMS_QUIZ_TYPES: readonly QuizTypeEnum[] = [
  QuizTypeEnum.OPEN_QUESTION,
  QuizTypeEnum.MULTIPLE_CHOICE_QUESTION,
  QuizTypeEnum.ASSOCIATION_QUESTION,
  QuizTypeEnum.GAP_FILLING_QUESTION,
];
export const FREE_QUIZ_TYPES: readonly QuizTypeEnum[] = [
  QuizTypeEnum.RATING,
  QuizTypeEnum.OPEN_QUESTION,
  QuizTypeEnum.MULTIPLE_CHOICE_UNSCORED,
];
export const SCORED_QUIZ_TYPES: readonly QuizTypeEnum[] = QUIZ_TYPES.filter((type) => QUIZ_TYPE_META[type].scored);

export const isQuizType = (type?: string | null): type is QuizTypeEnum =>
  QUIZ_TYPES.some((quizType) => quizType === type);

export const isScoredQuizType = (type?: string | null): boolean => isQuizType(type) && QUIZ_TYPE_META[type].scored;

export const ACTIVITY_TYPE_META: Record<ActivitiesResponseTypeEnum, IActivityTypeMeta> = {
  [ActivitiesResponseTypeEnum.Text]: { label: "Texte", icon: "text" },
  [ActivitiesResponseTypeEnum.Document]: { label: "Document", icon: "document" },
  [ActivitiesResponseTypeEnum.Video]: { label: "Vidéo", icon: "video" },
  [ActivitiesResponseTypeEnum.Quiz]: { label: "Question", icon: "quiz" },
  [ActivitiesResponseTypeEnum.Embed]: { label: "Code embed", learnerLabel: "Embed", icon: "embed" },
  [ActivitiesResponseTypeEnum.Scorm]: { label: "Module SCORM", learnerLabel: "Module intégré", icon: "scorm" },
};

export const LMS_ACTIVITY_TYPES: readonly ActivitiesResponseTypeEnum[] = [
  ActivitiesResponseTypeEnum.Text,
  ActivitiesResponseTypeEnum.Document,
  ActivitiesResponseTypeEnum.Video,
  ActivitiesResponseTypeEnum.Quiz,
  ActivitiesResponseTypeEnum.Embed,
  ActivitiesResponseTypeEnum.Scorm,
];
// The supports a questionnaire intercalates between its questions.
export const FORM_SUPPORT_TYPES: readonly ActivitiesResponseTypeEnum[] = [
  ActivitiesResponseTypeEnum.Text,
  ActivitiesResponseTypeEnum.Video,
  ActivitiesResponseTypeEnum.Document,
];

export const isActivityType = (type?: string | null): type is ActivitiesResponseTypeEnum =>
  Object.values(ActivitiesResponseTypeEnum).some((activityType) => activityType === type);

// What keys an activité or an answer row a builder made, until the server numbers it.
export const newDraftId = (): string => uuidv4();

/**
 * A blank answer row in the shape its question type reads: a scored choice is marked wrong, an association pair has its
 * prompt. A texte à trou has none: its rows come from the blanks marked in its sentence.
 */
export function blankAnswer(subType: string, order: number): ActivitiesAnswerResponse {
  return {
    answerId: newDraftId(),
    order,
    answer: "",
    ...(subType === QuizTypeEnum.MULTIPLE_CHOICE_QUESTION ? { isRightAnswer: false } : {}),
    ...(subType === QuizTypeEnum.ASSOCIATION_QUESTION ? { question: "" } : {}),
  };
}

// A texte à trou marks its blanks inline as [0], [1], …
const GAP_MARKERS = /\[(\d+)\]/g;

export type QuizGapSegment =
  { kind: QuizGapSegmentKindEnum.TEXT; value: string } | { kind: QuizGapSegmentKindEnum.GAP; order: number };

/** The sentence of a texte à trou, cut around its blanks. A marker `isGap` rejects stays literal text, as players show it. */
export function splitGapText(text: string, isGap: (order: number) => boolean = () => true): QuizGapSegment[] {
  // Odd indices of a split-with-capture are the captured marker numbers, even ones the literal text.
  return text.split(GAP_MARKERS).map((part, index): QuizGapSegment => {
    if (index % 2 === 0) return { kind: QuizGapSegmentKindEnum.TEXT, value: part };
    const order = Number(part);
    return isGap(order)
      ? { kind: QuizGapSegmentKindEnum.GAP, order }
      : { kind: QuizGapSegmentKindEnum.TEXT, value: `[${part}]` };
  });
}

/**
 * A bracketed number the author typed, made inert with a zero-width space: saved as is, it would read as a marker and
 * collide with the blanks numbered from 0.
 */
export const escapeGapMarkers = (text: string): string => text.replace(GAP_MARKERS, "[\u200B$1]");
